/**
 * NOS-63: Custom domain settings UI.
 * Add, verify, and remove custom domains with DNS instructions and status indicators.
 */
import { useState, useCallback } from "react"
import { toast } from "sonner"
import { Check, Copy, Loader2, Lock, AlertTriangle, XCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  useCustomDomain,
  type DnsInstructions,
} from "@/hooks/useCustomDomain"
import { validateCustomDomain } from "@/lib/domain-validation"
import { ROOT_DOMAIN } from "@/lib/vercel-domains"

const HELP_DOCS_URL = "https://vercel.com/docs/concepts/projects/domains"

interface CustomDomainSettingsProps {
  orgId: string
  orgSlug: string
}

function CopyableRecord({
  label,
  value,
}: {
  label: string
  value: string
}) {
  const [copied, setCopied] = useState(false)
  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      toast.success("Copied to clipboard")
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error("Failed to copy")
    }
  }, [value])
  return (
    <div className="flex items-center gap-2 rounded-md border bg-muted/50 p-3">
      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
        <p className="font-mono text-sm break-all">{value}</p>
      </div>
      <Button variant="outline" size="sm" onClick={copy} className="shrink-0">
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </Button>
    </div>
  )
}

export function CustomDomainSettings({ orgId, orgSlug }: CustomDomainSettingsProps) {
  const {
    domain,
    isLoading,
    isBusy,
    error,
    addDomain,
    verifyDomain,
    removeDomain,
  } = useCustomDomain(orgId)

  const [domainInput, setDomainInput] = useState("")
  const [validationError, setValidationError] = useState<string | null>(null)
  const [dnsInstructions, setDnsInstructions] = useState<DnsInstructions | null>(null)
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false)

  const defaultDomain = `${orgSlug}.${ROOT_DOMAIN}`

  const handleAddDomain = useCallback(async () => {
    const result = validateCustomDomain(domainInput)
    if (!result.valid) {
      setValidationError(result.error ?? "Invalid domain")
      return
    }
    setValidationError(null)
    const res = await addDomain(domainInput, orgId)
    if (res.ok && res.dnsInstructions) {
      setDnsInstructions(res.dnsInstructions)
      setDomainInput("")
      toast.success("Domain added. Add the DNS records below.")
    } else if (!res.ok && res.error) {
      toast.error(res.error)
    }
  }, [domainInput, orgId, addDomain])

  const handleVerify = useCallback(async () => {
    if (!domain?.domain) return
    const res = await verifyDomain(domain.domain, orgId)
    if (res.ok) {
      toast.success("Domain verified successfully")
      setDnsInstructions(null)
    } else if (!res.ok && res.error) {
      toast.error(res.error)
    }
  }, [domain?.domain, orgId, verifyDomain])

  const handleRemove = useCallback(async () => {
    if (!domain?.domain) return
    const res = await removeDomain(domain.domain, orgId)
    if (res.ok) {
      toast.success("Custom domain removed")
      setRemoveDialogOpen(false)
      setDnsInstructions(null)
    } else if (!res.ok && res.error) {
      toast.error(res.error)
    }
  }, [domain?.domain, orgId, removeDomain])

  const showTroubleshooting =
    domain &&
    (domain.verification_status === "pending" || domain.verification_status === "failed")

  const instructions = dnsInstructions ?? (domain?.dns_records as DnsInstructions | null)

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Custom Domain</CardTitle>
          <CardDescription>Loading domain settings…</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading…</span>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Custom Domain</CardTitle>
        <CardDescription>
          Your default website URL is{" "}
          <span className="font-medium text-foreground">{defaultDomain}</span>
          . Add a custom domain (e.g. donate.example.org) to brand your site.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {!domain ? (
          <>
            <div className="space-y-2">
              <Label htmlFor="custom-domain-input">Add Custom Domain</Label>
              <div className="flex gap-2">
                <Input
                  id="custom-domain-input"
                  value={domainInput}
                  onChange={(e) => {
                    setDomainInput(e.target.value)
                    setValidationError(null)
                  }}
                  placeholder="donate.example.org"
                  className="font-mono"
                  disabled={isBusy}
                  onKeyDown={(e) => e.key === "Enter" && handleAddDomain()}
                />
                <Button onClick={handleAddDomain} disabled={isBusy}>
                  {isBusy ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Adding…
                    </>
                  ) : (
                    "Add Domain"
                  )}
                </Button>
              </div>
              {validationError && (
                <p className="text-sm text-destructive">{validationError}</p>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-sm font-medium">{domain.domain}</span>
              <Badge
                variant={
                  domain.verification_status === "verified"
                    ? "default"
                    : domain.verification_status === "failed"
                      ? "destructive"
                      : "secondary"
                }
                className={
                  domain.verification_status === "pending"
                    ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200"
                    : ""
                }
              >
                {domain.verification_status === "verified" && (
                  <Check className="mr-1 h-3 w-3" />
                )}
                {domain.verification_status === "failed" && (
                  <XCircle className="mr-1 h-3 w-3" />
                )}
                {domain.verification_status === "pending" && (
                  <AlertTriangle className="mr-1 h-3 w-3" />
                )}
                {domain.verification_status}
              </Badge>
              <Badge
                variant="outline"
                className={
                  domain.ssl_status === "active"
                    ? "border-green-500 text-green-700 dark:text-green-400"
                    : domain.ssl_status === "failed"
                      ? "border-destructive text-destructive"
                      : ""
                }
              >
                {domain.ssl_status === "pending" && (
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                )}
                {domain.ssl_status === "active" && (
                  <Lock className="mr-1 h-3 w-3" />
                )}
                {domain.ssl_status === "failed" && (
                  <XCircle className="mr-1 h-3 w-3" />
                )}
                SSL: {domain.ssl_status}
              </Badge>
            </div>

            {instructions && (
              <div className="space-y-2">
                <p className="text-sm font-medium">DNS records to add</p>
                <p className="text-xs text-muted-foreground">
                  Add these records at your DNS provider. Propagation can take up to 48 hours.
                </p>
                <div className="space-y-2">
                  <CopyableRecord
                    label="CNAME"
                    value={`${instructions.cname.name} → ${instructions.cname.value}`}
                  />
                  <CopyableRecord
                    label="TXT (verification)"
                    value={instructions.txt.value}
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleVerify}
                  disabled={isBusy}
                >
                  {isBusy ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Checking…
                    </>
                  ) : (
                    "Check Verification"
                  )}
                </Button>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setRemoveDialogOpen(true)}
                disabled={isBusy}
              >
                Remove Domain
              </Button>
            </div>

            {showTroubleshooting && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950/30">
                <p className="font-medium text-amber-800 dark:text-amber-200">
                  Troubleshooting tips
                </p>
                <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-amber-700 dark:text-amber-300">
                  <li>Ensure DNS records match exactly (no trailing dots)</li>
                  <li>Propagation can take up to 48 hours — wait and try again</li>
                  <li>For apex domains (example.org), use ALIAS or A records per your provider</li>
                  <li>
                    <a
                      href={HELP_DOCS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:no-underline"
                    >
                      Vercel domain help docs
                    </a>
                  </li>
                </ul>
              </div>
            )}
          </>
        )}
      </CardContent>

      <AlertDialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove custom domain?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove {domain?.domain} from your organization. Your site will
              revert to {defaultDomain}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBusy}>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={handleRemove}
              disabled={isBusy}
            >
              {isBusy ? "Removing…" : "Remove Domain"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
